import * as _ from 'lodash';
import { useState } from 'react';
import { Icon, Heading, Text, TextField, Flex, Button, Box, Toggle } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Dropdown } from '@vibe/core';
import { type ColumnConfig, type AggregateConfig } from '@gorilla/portal-shared/src/schemas';
import { Settings } from '@vibe/icons';
import { ExternalLink } from './ExternalLink';
import { createRandomId } from '../lib/utils';
import {
  BOARD_COLUMNS_META,
  DATE_FORMATS,
  TIME_FORMATS,
  MIRROR_COLUMN_FORMATTERS,
  NUMBER_FORMAT_LOCALES,
  NUMBER_FORMAT_STYLES,
} from '@gorilla/portal-shared/src/consts';

const GRID_LABEL_VISIBILITY_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'show', label: 'Show' },
  { value: 'hide', label: 'Hide' },
];

const AGGREGATE_FUNCTION_OPTIONS = [
  { value: 'sum', label: 'Sum', endElement: { type: 'suffix', value: 'SUM' } },
  { value: 'avg', label: 'Average', endElement: { type: 'suffix', value: 'AVG' } },
  { value: 'min', label: 'Minimum', endElement: { type: 'suffix', value: 'MIN' } },
  { value: 'max', label: 'Maximum', endElement: { type: 'suffix', value: 'MAX' } },
  { value: 'count', label: 'Count', endElement: { type: 'suffix', value: 'COUNT' } },
  { value: 'median', label: 'Median', endElement: { type: 'suffix', value: 'MED' } },
];

export const hasConfigOptionsSet = (column: any, columnConfig: ColumnConfig, mode?: 'grid' | 'table' | 'clientitem') => {
  if (!columnConfig) return false;
  // @ts-ignore
  const supportedOptions: string[] = BOARD_COLUMNS_META[column.type]?.supported_options || [];

  if (columnConfig.custom_title) return true;
  if (mode === 'grid' && columnConfig.grid_label_visibility && columnConfig.grid_label_visibility !== 'auto') return true;
  if (supportedOptions.includes('date_format') && columnConfig.date_format && columnConfig.date_format !== DATE_FORMATS[0].value)
    return true;
  if (
    supportedOptions.includes('time_format') &&
    ((columnConfig.time_format && columnConfig.time_format !== TIME_FORMATS[0].value) || columnConfig.disable_time)
  )
    return true;
  if (supportedOptions.includes('enable_file_viewer') && columnConfig.enable_file_viewer) return true;
  if (supportedOptions.includes('number_format') && columnConfig.enable_custom_number_format) return true;
  if (supportedOptions.includes('mirror_column_formatter') && columnConfig.mirror_column_formatter) return true;
  if (supportedOptions.includes('disable_tag_splitting') && columnConfig.disable_tag_splitting) return true;
  if ((mode === 'table' || mode === 'grid') && supportedOptions.includes('enable_compact_mode') && columnConfig.enable_compact_mode)
    return true;

  return false;
};

type ColumnConfigModalProps = {
  show: boolean;
  columnConfig: ColumnConfig;
  column: any;
  mode?: 'grid' | 'table' | 'clientitem';
  aggregate?: AggregateConfig | null;
  showAggregate?: boolean;
  onClose: (result?: { config: ColumnConfig; aggregate?: AggregateConfig }) => void;
};

export function ColumnConfigModal({ show, column, mode, onClose, columnConfig = {}, aggregate, showAggregate }: ColumnConfigModalProps) {
  const [draft, setDraft] = useState(() => _.cloneDeep(columnConfig));
  const [aggregateFunction, setAggregateFunction] = useState<AggregateConfig['function'] | null>(aggregate?.function ?? null);
  // @ts-ignore
  const supportedOptions = BOARD_COLUMNS_META[column.type]?.supported_options || [];
  let selectedDateFormatOption = DATE_FORMATS[0];

  if (draft.date_format && DATE_FORMATS.find((option) => option.value === draft.date_format)) {
    selectedDateFormatOption = DATE_FORMATS.find((option) => option.value === draft.date_format)!;
  }

  let selectedTimeFormatOption = TIME_FORMATS[0];

  if (draft.time_format && TIME_FORMATS.find((option) => option.value === draft.time_format)) {
    selectedTimeFormatOption = TIME_FORMATS.find((option) => option.value === draft.time_format)!;
  }

  // for now we don't allow the progress formatter, unless it is already set
  const availableMirrorColumnFormatterOptions =
    draft.mirror_column_formatter === 'progress'
      ? MIRROR_COLUMN_FORMATTERS
      : MIRROR_COLUMN_FORMATTERS.filter((option) => option.value !== 'progress');

  let selectedMirrorColumnFormatterOption = null;

  if (
    draft.mirror_column_formatter &&
    availableMirrorColumnFormatterOptions.find((option) => option.value === draft.mirror_column_formatter)
  ) {
    // @ts-ignore
    selectedMirrorColumnFormatterOption = availableMirrorColumnFormatterOptions.find(
      (option) => option.value === draft.mirror_column_formatter,
    )!;
  }

  const selectedGridLabelVisibilityOption =
    GRID_LABEL_VISIBILITY_OPTIONS.find((option) => option.value === draft.grid_label_visibility) || GRID_LABEL_VISIBILITY_OPTIONS[0];

  return (
    <AppModal
      contentSpacing={false}
      alertDialog={true}
      width={400}
      data-testid="columnConfigModal"
      show={show}
      onClose={() => {
        onClose();
      }}
    >
      <AppModalContent>
        <Box marginBottom="large">
          <Flex gap={8} style={{ marginBottom: '16px' }}>
            <Icon icon={Settings} size={24} style={{ transform: 'translate3d(0,-1px,0)' }} />
            <Heading type="h3" weight="bold" align="start" style={{ maxWidth: '250px' }}>
              {column.title} settings
            </Heading>
          </Flex>
          <Flex gap={8} direction="column" align="start">
            <Box style={{ width: '100%' }}>
              <div>
                <TextField
                  value={draft.custom_title || ''}
                  size="medium"
                  title="Custom column name"
                  placeholder={column.title}
                  onChange={(value) => {
                    setDraft({
                      ...draft,
                      custom_title: value || undefined,
                    });
                  }}
                />
              </div>
              {mode === 'grid' ? (
                <div style={{ marginTop: '16px' }}>
                  <Text type="text2" style={{ marginBottom: '4px' }}>
                    Column name visibility
                  </Text>
                  <Dropdown
                    onChange={(newOption: any) => {
                      setDraft({ ...draft, grid_label_visibility: newOption.value });
                    }}
                    placeholder="Choose visibility"
                    size="medium"
                    options={GRID_LABEL_VISIBILITY_OPTIONS}
                    value={dropdownValue(selectedGridLabelVisibilityOption)}
                    clearable={false}
                  />
                </div>
              ) : null}
              {supportedOptions.includes('date_format') ? (
                <div style={{ marginTop: '16px' }}>
                  <Text type="text2" style={{ marginBottom: '4px' }}>
                    Date format
                  </Text>
                  <Dropdown
                    onChange={(newOption: any) => {
                      setDraft({
                        ...draft,
                        date_format: newOption.value,
                      });
                    }}
                    placeholder="Choose format"
                    size="medium"
                    options={DATE_FORMATS}
                    value={dropdownValue(selectedDateFormatOption)}
                    clearable={false}
                  />
                </div>
              ) : null}
              {supportedOptions.includes('time_format') ? (
                <Flex direction="row" align="end" gap={16} style={{ marginTop: '16px', width: '100%' }}>
                  <div style={{ flex: 1 }}>
                    <Text type="text2" style={{ marginBottom: '4px' }}>
                      Time format
                    </Text>
                    <Dropdown
                      onChange={(newOption: any) => {
                        setDraft({
                          ...draft,
                          time_format: newOption.value,
                        });
                      }}
                      placeholder="Choose format"
                      size="medium"
                      options={TIME_FORMATS}
                      value={dropdownValue(selectedTimeFormatOption)}
                      clearable={false}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Box
                      paddingEnd="medium"
                      paddingStart="medium"
                      border
                      borderColor="uiBorderColor"
                      rounded="small"
                      style={{
                        marginTop: '16px',
                        paddingTop: '9px',
                        paddingBottom: '9px',
                      }}
                    >
                      <Flex gap={12}>
                        <Box
                          style={{
                            marginRight: '-8px',
                            marginLeft: '-8px',
                          }}
                        >
                          <Toggle
                            offOverrideText=""
                            onOverrideText=""
                            isSelected={!!draft.disable_time}
                            onChange={(newValue: boolean) => {
                              // Change to show or hide time
                              setDraft({
                                ...draft,
                                disable_time: newValue,
                              });
                            }}
                            size="small"
                          />
                        </Box>
                        <Text type="text2" color={!!draft.disable_time ? 'primary' : 'secondary'}>
                          Hide time value
                        </Text>
                      </Flex>
                    </Box>
                  </div>
                </Flex>
              ) : null}
              {supportedOptions.includes('enable_file_viewer') ? (
                <Box
                  paddingEnd="medium"
                  paddingStart="medium"
                  border
                  borderColor="uiBorderColor"
                  rounded="small"
                  style={{
                    marginTop: '18px',
                    paddingTop: draft.enable_file_viewer ? '16px' : '10px',
                    paddingBottom: draft.enable_file_viewer ? '16px' : '10px',
                  }}
                >
                  <div>
                    <Flex gap={12}>
                      <Box
                        style={{
                          marginRight: '-8px',
                          marginLeft: '-8px',
                        }}
                      >
                        <Toggle
                          offOverrideText=""
                          onOverrideText=""
                          isSelected={!!draft.enable_file_viewer}
                          onChange={(newValue: boolean) => {
                            // Change to show or hide file viewer
                            setDraft({
                              ...draft,
                              enable_file_viewer: newValue,
                            });
                          }}
                          size="small"
                        />
                      </Box>
                      <Text type="text2" color={!!draft.enable_file_viewer ? 'primary' : 'secondary'}>
                        Enable file preview
                      </Text>
                      <div style={{ marginLeft: 'auto' }}>
                        <Text type="text3" color="secondary" ellipsis={false}>
                          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/user-guides/file-viewer">
                            Learn more
                          </ExternalLink>
                        </Text>
                      </div>
                    </Flex>
                    {draft.enable_file_viewer ? (
                      <>
                        <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '12px' }}>
                          <strong style={{ display: 'inline-block', marginBottom: '4px' }}>Supported file extensions</strong>
                          <br />
                          Images: JPG, PNG, APNG, GIF, SVG, WebP, AVIF
                          <br />
                          Videos: MP4, WebM
                          <br />
                          Documents: PDF (Experimental)
                        </Text>
                      </>
                    ) : null}
                  </div>
                </Box>
              ) : null}

              {supportedOptions.includes('disable_tag_splitting') ? (
                <Box
                  paddingEnd="medium"
                  paddingStart="medium"
                  border
                  borderColor="uiBorderColor"
                  rounded="small"
                  style={{
                    marginTop: '18px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                  }}
                >
                  <div>
                    <Flex gap={12}>
                      <Box
                        style={{
                          marginRight: '-8px',
                          marginLeft: '-8px',
                        }}
                      >
                        <Toggle
                          offOverrideText=""
                          onOverrideText=""
                          isSelected={!!draft.disable_tag_splitting}
                          onChange={(newValue: boolean) => {
                            // Change to toggle disable tag splitting
                            setDraft({
                              ...draft,
                              disable_tag_splitting: newValue,
                            });
                          }}
                          size="small"
                        />
                      </Box>
                      <Text type="text2" color={!!draft.disable_tag_splitting ? 'primary' : 'secondary'}>
                        Disable item splitting into tags
                      </Text>
                      <div style={{ marginLeft: 'auto' }}>
                        <Text type="text3" color="secondary" ellipsis={false}>
                          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/features/builder/content-sections#anchor-7576">
                            Learn more
                          </ExternalLink>
                        </Text>
                      </div>
                    </Flex>
                    {draft.disable_tag_splitting ? (
                      <>
                        <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '12px' }}>
                          Keep this option enabled when <strong>linked item names</strong> likely <strong>contain commas</strong>, or you
                          lack control over the item names. This will turn item name tags into a comma separated text to avoid incorrect
                          display.
                        </Text>
                      </>
                    ) : null}
                  </div>
                </Box>
              ) : null}

              {/* Additional condition for Mirror columns and formatter type */}

              {(mode === 'table' || mode === 'grid') && supportedOptions.includes('enable_compact_mode') ? (
                <Box
                  paddingEnd="medium"
                  paddingStart="medium"
                  border
                  borderColor="uiBorderColor"
                  rounded="small"
                  style={{
                    marginTop: '18px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                  }}
                >
                  <div>
                    <Flex gap={12}>
                      <Box
                        style={{
                          marginRight: '-8px',
                          marginLeft: '-8px',
                        }}
                      >
                        <Toggle
                          offOverrideText=""
                          onOverrideText=""
                          isSelected={!!draft.enable_compact_mode}
                          onChange={(newValue: boolean) => {
                            // Change to toggle compact mode
                            setDraft({
                              ...draft,
                              enable_compact_mode: newValue,
                            });
                          }}
                          size="small"
                        />
                      </Box>
                      <Text type="text2" color={!!draft.enable_compact_mode ? 'primary' : 'secondary'}>
                        Enable compact mode
                      </Text>
                      <div style={{ marginLeft: 'auto' }}>
                        <Text type="text3" color="secondary" ellipsis={false}>
                          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/features/builder/content-sections#anchor-7576">
                            Learn more
                          </ExternalLink>
                        </Text>
                      </div>
                    </Flex>
                    {draft.enable_compact_mode ? (
                      <>
                        <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '12px' }}>
                          Keep this option enabled when there are many single values in this column, and you want to limit the initial
                          display to the first value followed by a +N button for reasons of readability and space.
                        </Text>
                      </>
                    ) : null}
                  </div>
                </Box>
              ) : null}

              {supportedOptions.includes('number_format') ? (
                <Box
                  paddingEnd="medium"
                  paddingStart="medium"
                  border
                  borderColor="uiBorderColor"
                  rounded="small"
                  style={{
                    marginTop: '18px',
                    paddingTop: draft.enable_custom_number_format ? '16px' : '10px',
                    paddingBottom: '10px',
                  }}
                >
                  <div>
                    <Flex gap={12}>
                      <Box style={{ marginRight: '-8px', marginLeft: '-8px' }}>
                        <Toggle
                          offOverrideText=""
                          onOverrideText=""
                          isSelected={!!draft.enable_custom_number_format}
                          onChange={(newValue: boolean) => {
                            setDraft({ ...draft, enable_custom_number_format: newValue });
                          }}
                          size="small"
                        />
                      </Box>
                      <Text type="text2" color={!!draft.enable_custom_number_format ? 'primary' : 'secondary'}>
                        Enable custom number format
                      </Text>
                      <div style={{ marginLeft: 'auto' }}>
                        <Text type="text3" color="secondary" ellipsis={false}>
                          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/features/builder/content-sections#anchor-7576">
                            Learn more
                          </ExternalLink>
                        </Text>
                      </div>
                    </Flex>
                    {draft.enable_custom_number_format ? (
                      <>
                        <Flex
                          direction="row"
                          gap={16}
                          style={{
                            marginTop: '16px',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <Text type="text2" style={{ marginBottom: '4px' }}>
                              Locale
                            </Text>
                            <Dropdown
                              onChange={(newOption: any) => {
                                setDraft({ ...draft, number_format_locale: newOption?.value });
                              }}
                              placeholder="Choose locale"
                              size="medium"
                              options={NUMBER_FORMAT_LOCALES}
                              value={
                                NUMBER_FORMAT_LOCALES.find((o) => o.value === (draft.number_format_locale || 'en-US')) ||
                                NUMBER_FORMAT_LOCALES[0]
                              }
                              clearable={false}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Text type="text2" style={{ marginBottom: '4px' }}>
                              Style
                            </Text>
                            <Dropdown
                              onChange={(newOption: any) => {
                                setDraft({ ...draft, number_format_style: newOption?.value });
                              }}
                              placeholder="Choose style"
                              size="medium"
                              options={NUMBER_FORMAT_STYLES}
                              value={
                                NUMBER_FORMAT_STYLES.find((o) => o.value === (draft.number_format_style || 'decimal')) ||
                                NUMBER_FORMAT_STYLES[0]
                              }
                              clearable={false}
                            />
                          </div>
                        </Flex>
                        {draft.number_format_style === 'currency' ? (
                          <div style={{ marginTop: '16px' }}>
                            <TextField
                              value={draft.number_format_currency || 'USD'}
                              size="medium"
                              title="Currency code"
                              placeholder="e.g. USD, EUR"
                              onChange={(value) => {
                                setDraft({ ...draft, number_format_currency: value || undefined });
                              }}
                            />
                          </div>
                        ) : null}
                        {draft.number_format_style === 'unit' ? (
                          <div style={{ marginTop: '16px' }}>
                            <TextField
                              value={draft.number_format_unit || ''}
                              size="medium"
                              title="Unit"
                              placeholder="e.g. kilogram, liter"
                              onChange={(value) => {
                                setDraft({ ...draft, number_format_unit: value || undefined });
                              }}
                            />
                          </div>
                        ) : null}
                        <Flex
                          direction="row"
                          gap={16}
                          style={{
                            marginTop: '16px',
                            marginBottom: '8px',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <TextField
                              value={draft.number_format_min_fraction_digits != null ? String(draft.number_format_min_fraction_digits) : ''}
                              size="medium"
                              title="Min fraction digits"
                              placeholder="e.g. 0"
                              onChange={(value) => {
                                setDraft({
                                  ...draft,
                                  number_format_min_fraction_digits: value !== '' ? parseInt(value, 10) || 0 : undefined,
                                });
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <TextField
                              value={draft.number_format_max_fraction_digits != null ? String(draft.number_format_max_fraction_digits) : ''}
                              size="medium"
                              title="Max fraction digits"
                              placeholder="e.g. 2"
                              onChange={(value) => {
                                setDraft({
                                  ...draft,
                                  number_format_max_fraction_digits: value !== '' ? parseInt(value, 10) || 0 : undefined,
                                });
                              }}
                            />
                          </div>
                        </Flex>
                      </>
                    ) : null}
                  </div>
                </Box>
              ) : null}

              {showAggregate && column.type === 'numbers' ? (
                <Box
                  paddingEnd="medium"
                  paddingStart="medium"
                  border
                  borderColor="uiBorderColor"
                  rounded="small"
                  style={{ marginTop: '18px', paddingTop: '12px', paddingBottom: '12px' }}
                >
                  <div style={{ width: '100%' }}>
                    <Flex gap={12} style={{ marginBottom: '10px' }}>
                      <Text type="text2">Column summary</Text>
                      <div style={{ marginLeft: 'auto' }}>
                        <Text type="text3" color="secondary" ellipsis={false}>
                          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/features/builder/content-sections#anchor-7576">
                            Learn more
                          </ExternalLink>
                        </Text>
                      </div>
                    </Flex>
                    <Dropdown
                      onChange={(newOption: any) => {
                        setAggregateFunction(newOption ? (newOption.value as AggregateConfig['function']) : null);
                      }}
                      placeholder="Choose a function"
                      size="medium"
                      options={AGGREGATE_FUNCTION_OPTIONS}
                      value={AGGREGATE_FUNCTION_OPTIONS.find((option) => option.value === aggregateFunction) || null}
                      clearable={true}
                    />
                    <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '12px' }}>
                      Calculate a summary value for this number column across items matching applied filters. The result is shown in the
                      table footer.
                    </Text>
                  </div>
                </Box>
              ) : null}

              {supportedOptions.includes('mirror_column_formatter') ? (
                <div style={{ marginTop: '16px', width: '100%' }}>
                  <Text type="text2" style={{ marginBottom: '4px' }}>
                    Value formatter
                  </Text>
                  <Dropdown
                    onChange={(newOption: any) => {
                      setDraft({
                        ...draft,
                        mirror_column_formatter: newOption ? newOption.value : undefined,
                      });
                    }}
                    placeholder="Choose type"
                    size="medium"
                    options={availableMirrorColumnFormatterOptions}
                    value={dropdownValue(selectedMirrorColumnFormatterOption)}
                    clearable={true}
                    className="custom_dropdown"
                  />
                </div>
              ) : null}

              {supportedOptions.includes('mirror_column_formatter') && draft.mirror_column_formatter === 'progress' ? (
                <div style={{ marginTop: '16px', width: '100%' }}>
                  <TextField
                    value={draft.mirror_column_done_status || ''}
                    size="medium"
                    title="Target label"
                    placeholder="e.g. Done"
                    onChange={(value) => {
                      setDraft({
                        ...draft,
                        mirror_column_done_status: value,
                      });
                    }}
                  />
                </div>
              ) : null}

              {supportedOptions.includes('mirror_column_formatter') ? (
                <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '16px' }}>
                  Define how the mirrored values will be displayed in this column.{' '}
                  {draft.mirror_column_formatter === 'progress' ? (
                    <>
                      Choose <strong>Progress</strong> for status columns to show a progress bar based on a specific{' '}
                      <strong>Target label</strong> of the linked items.{' '}
                    </>
                  ) : null}
                  Choose <strong>Summarize linked items</strong> to sum up values of a mirrored number column.{' '}
                  {draft.mirror_column_formatter === 'progress' ? (
                    <>
                      <br />
                      <br />
                      <strong>
                        Please note that using the Progress formatter may impact performance because it will require fetching additional
                        data.
                      </strong>
                    </>
                  ) : null}
                </Text>
              ) : null}
            </Box>
          </Flex>
        </Box>
        <Box marginBottom="small">
          <Flex gap={16}>
            <Button
              size="medium"
              kind="primary"
              color="primary"
              style={{
                width: '100%',
              }}
              onClick={() => {
                const nextAggregate: AggregateConfig | undefined = aggregateFunction
                  ? { id: aggregate?.id || createRandomId(), function: aggregateFunction }
                  : undefined;
                onClose({ config: draft, aggregate: nextAggregate });
              }}
            >
              Save settings
            </Button>
          </Flex>
        </Box>
      </AppModalContent>
    </AppModal>
  );
}
